import React from 'react';
import { Package } from 'lucide-react';

interface ReceiptItem {
  name: string;
  reference_code?: string;
  category?: string;
  color?: string;
  size?: string;
  quantity: number;
  unit_price: number;
  total: number;
}

interface ReceiptProps {
  receiptNumber: string;
  date: string;
  items: ReceiptItem[];
  subtotal: number;
  tax?: number;
  total: number;
  paymentMethod?: string;
  companyInfo?: {
    name?: string;
    address?: string;
    phone?: string;
    email?: string;
    tax_id?: string;
    website?: string;
    logo_url?: string;
  };
}

export const Receipt: React.FC<ReceiptProps> = ({
  receiptNumber,
  date,
  items,
  subtotal,
  tax = 0,
  total,
  paymentMethod,
  companyInfo
}) => {
  return (
    <div className="receipt-print max-w-2xl mx-auto bg-white p-8 shadow-lg">
      {/* Header with Logo and Company Info */}
      <div className="border-b-2 border-gray-800 pb-6 mb-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            {companyInfo?.logo_url ? (
              <img
                src={companyInfo.logo_url}
                alt="Company Logo"
                className="h-20 w-auto object-contain mb-4"
              />
            ) : (
              <div className="flex items-center mb-4">
                <div className="bg-blue-600 p-2 rounded-lg mr-3">
                  <Package className="h-8 w-8 text-white" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {companyInfo?.name || 'Inventory Pro'}
                </h1>
              </div>
            )}
            {companyInfo?.name && companyInfo?.logo_url && (
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                {companyInfo.name}
              </h2>
            )}
            {companyInfo?.address && (
              <p className="text-sm text-gray-600">{companyInfo.address}</p>
            )}
            <div className="mt-2 space-y-1">
              {companyInfo?.phone && (
                <p className="text-sm text-gray-600">Tel: {companyInfo.phone}</p>
              )}
              {companyInfo?.email && (
                <p className="text-sm text-gray-600">Email: {companyInfo.email}</p>
              )}
              {companyInfo?.website && (
                <p className="text-sm text-gray-600">Web: {companyInfo.website}</p>
              )}
              {companyInfo?.tax_id && (
                <p className="text-sm text-gray-600">Tax ID: {companyInfo.tax_id}</p>
              )}
            </div>
          </div>
          <div className="text-right">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">RECEIPT</h2>
            <p className="text-sm text-gray-600">
              <span className="font-semibold">Receipt #:</span> {receiptNumber}
            </p>
            <p className="text-sm text-gray-600">
              <span className="font-semibold">Date:</span> {date}
            </p>
          </div>
        </div>
      </div>

      {/* Items Table */}
      <div className="mb-6 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2 border-gray-300">
              <th className="text-left py-2 font-semibold text-gray-700">Item</th>
              <th className="text-left py-2 font-semibold text-gray-700">Ref</th>
              <th className="text-left py-2 font-semibold text-gray-700">Category</th>
              <th className="text-left py-2 font-semibold text-gray-700">Color</th>
              <th className="text-left py-2 font-semibold text-gray-700">Size</th>
              <th className="text-center py-2 font-semibold text-gray-700">Qty</th>
              <th className="text-right py-2 font-semibold text-gray-700">Price</th>
              <th className="text-right py-2 font-semibold text-gray-700">Total</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={index} className="border-b border-gray-200">
                <td className="py-2.5 text-gray-800 font-medium">{item.name}</td>
                <td className="py-2.5 text-gray-600 font-mono text-xs">{item.reference_code || '-'}</td>
                <td className="py-2.5 text-gray-600">{item.category || '-'}</td>
                <td className="py-2.5 text-gray-600">{item.color || '-'}</td>
                <td className="py-2.5 text-gray-600">{item.size || '-'}</td>
                <td className="py-2.5 text-center text-gray-800">{item.quantity}</td>
                <td className="py-2.5 text-right text-gray-800">
                  ${item.unit_price.toFixed(2)}
                </td>
                <td className="py-2.5 text-right font-semibold text-gray-900">
                  ${item.total.toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div className="border-t-2 border-gray-300 pt-4 mb-6">
        <div className="flex justify-end mb-2">
          <div className="w-64">
            <div className="flex justify-between mb-2">
              <span className="text-gray-700">Subtotal:</span>
              <span className="font-medium text-gray-900">${subtotal.toFixed(2)}</span>
            </div>
            {tax > 0 && (
              <div className="flex justify-between mb-2">
                <span className="text-gray-700">Tax:</span>
                <span className="font-medium text-gray-900">${tax.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between border-t-2 border-gray-800 pt-2">
              <span className="text-lg font-bold text-gray-900">TOTAL:</span>
              <span className="text-lg font-bold text-gray-900">
                ${total.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Method */}
      {paymentMethod && (
        <div className="mb-6">
          <p className="text-sm text-gray-600">
            <span className="font-semibold">Payment Method:</span> {paymentMethod}
          </p>
        </div>
      )}

      {/* Footer */}
      <div className="border-t border-gray-300 pt-6 text-center">
        <p className="text-sm text-gray-600 mb-2">
          Thank you for your business!
        </p>
        <p className="text-xs text-gray-500">
          This is a computer-generated receipt and does not require a signature.
        </p>
      </div>
    </div>
  );
};
